<template>
  <div v-if="userData">
    <div class="container text-center d-flex flex-column justify-content-center align-items-center vh-100">
      <h2 class="mb-4 fw-bold">
        You are already logged in.
      </h2>
      <router-link to="/">
        <button class="btn btn-primary px-4 py-2">
          Go to Home Page
        </button>
      </router-link>
    </div>
  </div>

  <div
    v-else
    class="vh-100 d-flex align-items-center justify-content-center bg-light px-3"
  >
    <div class="login-card shadow-lg w-100 d-flex">
      <div class="row g-0 w-100 h-100">
        <!-- Left Panel (Empty space to let the background image show) -->
        <div
          class="col-md-7 d-none d-md-block"
          style="min-height: 100%;"
        >
          &nbsp;
        </div>

        <!-- Right Panel (Form) -->
        <div class="col-md-5 d-flex align-items-center justify-content-center">
          <div
            class="auth-form-container form-glass d-flex flex-column"
            style="gap: 24px;"
          >
            <div class="text-center">
              <h2 class="fw-bold mb-2">
                Welcome Back
              </h2>
              <p class="text-muted mb-0">
                Enter your details to access your account.
              </p>
            </div>

            <form
              class="d-flex flex-column"
              style="gap: 24px;"
              @submit.prevent="handleLogin"
            >
              <div>
                <label class="form-label text-muted small fw-semibold text-uppercase mb-1">Email Address</label>
                <input
                  v-model="email"
                  type="email"
                  class="form-control form-control-lg bg-white border-0 shadow-sm"
                  placeholder="e.g. hello@petqueen.com"
                  required
                >
              </div>
              
              <div>
                <label class="form-label text-muted small fw-semibold text-uppercase mb-1">Password</label>
                <div class="input-group shadow-sm rounded">
                  <input
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-control form-control-lg bg-white border-0"
                    placeholder="••••••••"
                    required
                  >
                  <button
                    type="button"
                    class="btn btn-white bg-white border-0 text-muted px-3"
                    @click="togglePassword"
                  >
                    <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" />
                  </button>
                </div>
              </div>

              <div
                v-if="error"
                class="alert alert-danger py-2 small mb-0"
              >
                {{ error }}
              </div>

              <button
                type="submit"
                class="btn btn-primary text-white w-100 py-3 fw-bold rounded-3 shadow-sm"
                :disabled="loading"
              >
                <span
                  v-if="loading"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Sign In
              </button>
            </form>

            <div class="d-flex align-items-center">
              <hr class="flex-grow-1 text-muted">
              <span class="mx-3 text-muted small">or continue with</span>
              <hr class="flex-grow-1 text-muted">
            </div>

            <button
              type="button"
              class="btn bg-white text-dark w-100 py-3 fw-bold rounded-3 shadow-sm d-flex align-items-center justify-content-center gap-2 border"
              :disabled="loading"
              @click="handleGoogleLogin"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                width="20"
                height="20"
              >
              Sign in with Google
            </button>

            <p class="text-center text-muted small mb-0">
              Don't have an account yet? 
              <router-link
                :to="{ path: '/signup', query: route.query }"
                class="text-primary text-decoration-none fw-bold"
              >
                Sign Up
              </router-link>
            </p>
            <div class="text-center">
              <router-link
                to="/"
                class="text-muted text-decoration-none small"
              >
                <i class="fas fa-arrow-left me-1" /> Back to Homepage
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase';
import { useRouter, useRoute } from 'vue-router';
import { doc, getDoc, collection, writeBatch } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';
import { storeToRefs } from 'pinia';

const userStore = useUserStore();
const { userData } = storeToRefs(userStore);

const email = ref('');
const password = ref('');
const error = ref(null);
const loading = ref(false);
const showPassword = ref(false);
const router = useRouter();
const route = useRoute();

const togglePassword = () => {
  showPassword.value = !showPassword.value;
};

const handleLogin = async () => {
  loading.value = true;
  error.value = null;
  try {
    await signInWithEmailAndPassword(auth, email.value, password.value);
    const redirectPath = route.query.redirect || '/';
    router.push(redirectPath);
  } catch (err) {
    if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
      error.value = 'Invalid email or password.';
    } else if (err.code === 'auth/too-many-requests') {
      error.value = 'Too many failed login attempts. Please try again later.';
    } else {
      error.value = 'An error occurred during login: ' + err.message;
    }
  } finally {
    loading.value = false;
  }
};

const handleGoogleLogin = async () => {
  loading.value = true;
  error.value = null;
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      const userData = {
        uid: user.uid,
        displayName: user.displayName || null,
        email: user.email,
        photoURL: user.photoURL,
        phoneNumber: user.phoneNumber || null,
        address: null,
        createdAt: new Date(),
        account_number: Date.now(),
      };

      await userStore.setUserData(user.uid, userData);

      const accountCollection = collection(db, 'users', user.uid, 'account');
      const accountDocs = [
        { id: 'properties', type: 'Member', ad_limit: 3 },
        { id: 'pet_account', type: 'Personal' },
        { id: 'service_account', type: 'Personal' },
      ];

      const batch = writeBatch(db);
      accountDocs.forEach((account) => {
        const docRef = doc(accountCollection, account.id);
        batch.set(docRef, account);
      });
      await batch.commit();
    } else {
      await userStore.fetchUserData(user.uid);
    }

    const redirectPath = route.query.redirect || '/';
    router.push(redirectPath);
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.login-card {
  max-width: 1000px;
  min-height: 750px;
  background-image: url('https://firebasestorage.googleapis.com/v0/b/petqueen-v3.firebasestorage.app/o/public%2Fassets%2FpetqueenLogin.jpeg?alt=media&token=f7581352-c414-4ddc-9882-e31af58bf227');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 20px;
  overflow: hidden;
}

.auth-form-container {
  width: 100%;
  max-width: 450px;
  padding: 24px;
}

/* Glass effect for mobile so form is readable over image */
.form-glass {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
}

/* Remove glass effect on desktop because the right side of the image is already blank/cream */
@media (min-width: 768px) {
  .form-glass {
    background: transparent;
    box-shadow: none;
  }
}

.form-control:focus {
  box-shadow: none;
  background-color: #fff !important;
  border: 1px solid #dee2e6 !important;
}

.btn-primary {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

.btn-primary:hover {
  background-color: #2563eb;
  border-color: #2563eb;
}
</style>
